// @givmo/board — the Schedule O draft's one read (CBG-YR5-SCHO; the backend's `board-schedule-o.md`
// §5). It takes the Form 990 page's address and answers the Form 990 read's own four refusals (a route
// miss, the type with no page, a stale year, a day it can't read) as answers, never errors
// (`form990-queries.ts`); anything else stays an error.

import { useQuery } from '@tanstack/react-query';
import { useBoardOperations } from '../../board-runtime';
import { refusalRead, type Form990Address, type Form990Refusal } from './form990-queries';
import { decodeScheduleODraft, type ScheduleODraft } from './schedule-o-decode';

export type ScheduleORead = { kind: 'page'; draft: ScheduleODraft } | Form990Refusal;

/** The draft's query keys, rooted per organization. */
export function boardScheduleOKeys(orgRef: string | null) {
  const root = ['board-schedule-o', orgRef ?? ''] as const;
  return {
    root,
    draft: (address: Form990Address) =>
      [...root, address.taxYearEndsOn, address.yearEndMonth, address.yearEndDay] as const,
  };
}

export function useScheduleODraft(orgRef: string | null, address: Form990Address, enabled = true) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardScheduleOKeys(orgRef).draft(address),
    enabled: enabled && orgRef !== null,
    // The refusals resolve as answers, so only a failure reaches here: retried once.
    retry: 1,
    queryFn: async (): Promise<ScheduleORead> => {
      try {
        const { data } = await operations.officerGetBoardScheduleODraft({
          path: { org_ref: orgRef ?? '' },
          query: {
            ...(address.taxYearEndsOn === null ? {} : { tax_year_ends_on: address.taxYearEndsOn }),
            ...(address.yearEndMonth === null ? {} : { year_end_month: address.yearEndMonth }),
            ...(address.yearEndDay === null ? {} : { year_end_day: address.yearEndDay }),
          },
          throwOnError: true,
        });
        const draft = decodeScheduleODraft(data);
        if (draft === null) throw new Error('The Schedule O draft could not be decoded.');
        return { kind: 'page', draft };
      } catch (error) {
        const answered = refusalRead(error, address);
        if (answered !== null) return answered;
        throw error;
      }
    },
  });
}
